/* -------------------------------------------------------------------------- */
/* Dependencies                                */
/* -------------------------------------------------------------------------- */

import { Button, Text, Title } from '@components/v2/ui';
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import {
	ActionIcon,
	Drawer,
	Flex,
	NumberFormatter,
	RangeSlider,
	Stack,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { getCurrencyUnit } from '@utils/currency-units';
import { cloneDeep } from '@utils/functions';
import isNil from 'lodash/isNil';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { useAdditionalityProject } from '../../hooks/useAdditionalityProjects';
import classes from './Filter-public-catalog.module.css';

/* -------------------------------------------------------------------------- */
/* Types                                    */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	'location.countryCode'?: { $in: string[] };
	technology?: string[];
	'financing.pledged_euro'?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	[key: string]: unknown;
};

type FilterProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/* Component                                  */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: FilterProps) => {
	const { t } = useTranslation();
	const {
		setAdditionalityProjectsQueryParams,
		setAdditionalityProjectsPage,
	} = useAdditionalityProject();
	const { data: countryCodes } = useCountryCodes(
		'additionalities'
	);

	const [fundedPartSlider, setFundedPartValueSlider] =
		useState<[number, number]>([0, 100000]);
	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const handleReset = () => {
		setAdditionalityProjectsQueryParams({});
		setFundedPartValueSlider([0, 100000]);
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const handleApplyFilters = () => {
		setAdditionalityProjectsQueryParams(queryParams);
		setAdditionalityProjectsPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	const handleCountryChange = (value: string[]) => {
		if (value.length === 0) {
			const clone = cloneDeep(queryParams);
			delete clone['location.countryCode'];
			setQueryParams(clone);
			return;
		}
		setQueryParams({
			...queryParams,
			'location.countryCode': { $in: value },
		});
	};

	const handleTechnologyChange = (value: string[]) => {
		if (value.length === 0) {
			const clone = cloneDeep(queryParams);
			delete clone.technology;
			setQueryParams(clone);
			return;
		}
		setQueryParams({ ...queryParams, technology: value });
	};

	const handleFundedPartChange = (
		value: [number, number]
	) => {
		setFundedPartValueSlider(value);

		if (value[0] === 0 && value[1] === 100000) {
			const clone = cloneDeep(queryParams);
			delete clone['financing.pledged_euro'];
			setQueryParams(clone);
		} else {
			setQueryParams({
				...queryParams,
				'financing.pledged_euro': {
					$gte: value[0],
					$lte:
						value[1] === 100000
							? 1000000000000000
							: value[1],
				},
			});
		}
	};

	const renderFundedPartLabel = (val: number) =>
		val === 0 || val === 100000 ? null : (
			<NumberFormatter
				value={val}
				thousandSeparator=" "
				decimalScale={2}
				suffix={` ${getCurrencyUnit('EUR')}`}
			/>
		);

	useEffect(() => {
		if (!opened) return;

		setQueryParams(cloneDeep(value ?? {}));

		const priceFilter = value?.['financing.pledged_euro'];
		setFundedPartValueSlider([
			priceFilter?.$gte ?? 0,
			priceFilter?.$lte === 1000000000000000
				? 100000
				: (priceFilter?.$lte ?? 100000),
		]);
	}, [opened, value]);

	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			<Flex
				align="center"
				justify="space-between"
				gap="16px"
				pb="var(--mantine-spacing-xl)"
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius="48px"
					variant="outline"
					size="48px"
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size="24px"
					/>
				</ActionIcon>
			</Flex>

			<Flex
				flex={1}
				direction="column"
				justify="space-between"
			>
				<Stack gap="var(--mantine-spacing-sm)">
					<MultiSelect
						placeholder={t('global.country')}
						w="100%"
						size="lg"
						searchable
						clearable
						comboboxProps={{ withinPortal: false }}
						data={filteredCountries}
						value={
							queryParams['location.countryCode']?.$in ??
							[]
						}
						onChange={handleCountryChange}
					/>

					<MultiSelect
						placeholder={t('global.technology')}
						w="100%"
						size="lg"
						searchable
						clearable
						comboboxProps={{ withinPortal: false }}
						data={PROJECT_TECHNOLOGIES}
						value={queryParams.technology ?? []}
						onChange={handleTechnologyChange}
					/>

					<Stack>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="button"
							mb="20px"
						>
							{t('global.fundedPart')}
						</Text>
						<RangeSlider
							value={fundedPartSlider}
							onChange={handleFundedPartChange}
							labelAlwaysOn
							min={0}
							max={100000}
							step={1}
							color="var(--mantine-color-igluPrimary-9)"
							label={renderFundedPartLabel}
							marks={[
								{ value: 0, label: '0€' },
								{ value: 100000, label: '100k€' },
							]}
						/>
					</Stack>
				</Stack>

				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
					mt="xl"
				>
					<Button
						fullWidth
						variant="outline"
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						fullWidth
						variant="primary"
						onClick={handleApplyFilters}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
